import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, ArrowLeft } from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";
import InstallPwaPrompt from "../components/InstallPwaPrompt";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useSEO } from "../lib/useSEO";

function readCachedBooking(id) {
  try {
    const raw = sessionStorage.getItem(`lh2o_booking_${id}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Validate that it's actually a booking object — otherwise treat as no cache.
    if (!parsed || (!parsed.id && !parsed.reference)) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

export default function BookingConfirmation() {
  const { id } = useParams();
  const { t } = useI18n();
  useSEO({ titleKey: "seo.confirm_title", descriptionKey: "seo.confirm_desc" });
  // Read sessionStorage synchronously — when arriving from the booking flow
  // we have the data already and render in the same paint cycle.
  const [booking, setBooking] = useState(() => readCachedBooking(id));

  useEffect(() => {
    // Always revalidate so latest data wins (status updates etc.)
    let alive = true;
    api
      .get(`/bookings/${id}`)
      .then((r) => {
        if (!alive) return;
        setBooking(r.data);
        try {
          sessionStorage.setItem(`lh2o_booking_${id}`, JSON.stringify(r.data));
        } catch (_) { /* ignore */ }
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [id]);

  return (
    <div
      className="min-h-screen bg-water-grad py-16"
      data-testid="booking-confirmation"
    >
      <div className="max-w-2xl mx-auto px-5">
        <div className="rounded-3xl bg-white border border-slate-200 shadow-lg p-10 text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 grid place-items-center mx-auto">
            <CheckCircle2 className="w-9 h-9 text-emerald-600" />
          </div>
          <h1 className="font-display mt-5 text-4xl font-bold tracking-tight">
            {t("booking.success_title")}
          </h1>
          <p className="mt-3 text-slate-600">{t("booking.success_text")}</p>

          {booking ? (
            <div className="mt-7 text-left rounded-2xl bg-slate-50 border border-slate-200 p-5 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">{t("booking.booking_id")}</span>
                <span
                  className="font-mono text-slate-900 font-bold tracking-wider"
                  data-testid="confirmation-reference"
                >
                  {booking.reference || booking.id.slice(0, 8).toUpperCase()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">{t("booking.pick_date")}</span>
                <span>
                  {booking.scheduled_date} · {booking.scheduled_time}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">{t("booking.address")}</span>
                <span>
                  {booking.address_street}, {booking.address_city}
                </span>
              </div>
              <div className="flex justify-between font-semibold pt-2 border-t border-slate-200">
                <span>{t("booking.total")}</span>
                <span>
                  {booking.total.toFixed(0)} {booking.currency}
                </span>
              </div>
            </div>
          ) : (
            <div
              className="mt-7 rounded-2xl bg-slate-50 border border-slate-200 p-5 space-y-3"
              data-testid="confirmation-skeleton"
              aria-hidden="true"
            >
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-2/5" />
            </div>
          )}

          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center items-center">
            <Link
              to="/portal"
              className="btn-primary inline-flex items-center gap-1.5"
              data-testid="confirmation-portal-cta"
            >
              {t("nav.portal")}
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sky-700 hover:text-sky-900 font-semibold"
              data-testid="confirmation-home-link"
            >
              <ArrowLeft className="w-4 h-4" /> {t("booking.back_home")}
            </Link>
          </div>
        </div>
      </div>
      <InstallPwaPrompt />
    </div>
  );
}
